import { CalendarIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/* value/onChange speak 'YYYY-MM-DD' (the wire format for DATE fields). parse
 * and format against local components so the calendar never shifts a day on a
 * negative-utc-offset client. */
function parseISODate(value: string): Date | undefined {
  if (!value) return undefined
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

function toISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'Pick a date'
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) {
  const selected = parseISODate(value)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          className={cn(
            'w-full justify-start text-left font-normal',
            !selected && 'text-ink-muted'
          )}
        >
          <CalendarIcon className='size-4' />
          {selected
            ? selected.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={selected}
          defaultMonth={selected}
          onSelect={date => {
            if (date) onChange(toISODate(date))
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}
